import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSubscription } from '../hooks/useSubscription'
import { deleteAccount, exportData, getLearningSettings, updateLearningSettings } from '../lib/api'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Slider from '@mui/material/Slider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ConfirmDialog from '../components/ConfirmDialog'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import type { LearningSettings } from '../types/models'
import { Eyebrow } from '../components/ui'

export default function Settings() {
  const { session, signOut, updatePassword } = useAuth()
  const { subscription, loading: subLoading, error: subError } = useSubscription()
  const navigate = useNavigate()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwError, setPwError] = useState<string | null>(null)
  const [pwSaved, setPwSaved] = useState(false)
  const [learningSettings, setLearningSettings] = useState<LearningSettings | null>(null)
  const [learningLoading, setLearningLoading] = useState(true)
  const [learningSaving, setLearningSaving] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  useEffect(() => {
    getLearningSettings()
      .then(s => { setLearningSettings(s); setLearningLoading(false) })
      .catch(() => setLearningLoading(false))
  }, [])

  async function handleUpdateLearningSettings(patch: Partial<LearningSettings>) {
    if (!learningSettings) return
    setLearningSaving(true)
    try {
      const updated = await updateLearningSettings(patch)
      setLearningSettings(updated)
    } finally {
      setLearningSaving(false)
    }
  }

  async function handleSavePassword(e: React.FormEvent) {
    e.preventDefault()
    setPwSaving(true)
    setPwError(null)
    setPwSaved(false)
    const { error } = await updatePassword(newPassword)
    setPwSaving(false)
    if (error) setPwError(error)
    else {
      setPwSaved(true)
      setNewPassword('')
    }
  }

  async function handleExport() {
    setExporting(true)
    setExportError(null)
    try {
      const { blob, filename } = await exportData()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Failed to export your data')
    } finally {
      setExporting(false)
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteAccount()
      await signOut()
      navigate('/')
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete account')
      setDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  const email = session?.user.email ?? ''
  const expiryFormatted = subscription?.expiryTime
    ? new Date(subscription.expiryTime).toLocaleDateString()
    : null

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto' }}>
      <Typography sx={{ fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 'clamp(26px,3.5vw,36px)', letterSpacing: '-0.6px', mb: 3 }}>
        Settings
      </Typography>

      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Eyebrow moss>Account</Eyebrow>
        </CardContent>
        <List disablePadding>
          <Divider />
          <ListItem>
            {/* overflowWrap: an email has no break points and can be one long run before the @. */}
            <ListItemText primary="Email" secondary={email} slotProps={{ secondary: { sx: { overflowWrap: 'anywhere' } } }} />
          </ListItem>
        </List>
        <CardContent sx={{ pt: 1 }}>
          <Button variant="outlined" onClick={signOut}>Sign out</Button>
        </CardContent>
      </Card>

      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Eyebrow moss>Password</Eyebrow>
        </CardContent>
        <CardContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set or change your password. Handy if you usually sign in with Google and also want an
            email + password option.
          </Typography>
          <Box component="form" onSubmit={handleSavePassword}>
            <Stack spacing={2}>
              <TextField
                label="New password"
                type="password"
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setPwSaved(false) }}
                autoComplete="new-password"
                fullWidth
              />
              {pwError && <ErrorMessage message={pwError} />}
              {pwSaved && <Typography variant="body2" color="success.main">Password updated.</Typography>}
              <Box>
                <Button variant="outlined" type="submit" disabled={pwSaving || !newPassword}>
                  {pwSaving ? 'Saving...' : 'Save password'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Eyebrow moss>Subscription</Eyebrow>
        </CardContent>
        {subLoading ? (
          <CardContent><LoadingSpinner /></CardContent>
        ) : subError ? (
          <CardContent><ErrorMessage message={subError} /></CardContent>
        ) : subscription?.active ? (
          <List disablePadding>
            <Divider />
            <ListItem>
              <ListItemText primary="Status" />
              <Chip label="Active" color="success" size="small" />
            </ListItem>
            {subscription.productId && (
              <>
                <Divider />
                <ListItem>
                  {/* productId is a store SKU string with no guaranteed break points. */}
                  <ListItemText primary="Plan" secondary={subscription.productId} slotProps={{ secondary: { sx: { overflowWrap: 'anywhere' } } }} />
                </ListItem>
              </>
            )}
            {expiryFormatted && (
              <>
                <Divider />
                <ListItem>
                  <ListItemText primary="Expires" secondary={expiryFormatted} />
                </ListItem>
              </>
            )}
          </List>
        ) : (
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>No active subscription.</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Subscribe via the Android app to unlock all features.</Typography>
            <Button
              variant="outlined"
              component="a"
              href="https://play.google.com/store/apps/details?id=com.recall.mobile"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get Recall on Google Play
              <span className="sr-only"> (opens in a new tab)</span>
            </Button>
          </CardContent>
        )}
      </Card>

      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ mb: 2 }}><Eyebrow moss>Learning</Eyebrow></Box>
          {learningLoading ? (
            <LoadingSpinner />
          ) : learningSettings ? (
            <>
              <Typography variant="body2" gutterBottom>
                Questions per session: {learningSettings.sessionSize}
              </Typography>
              <Slider
                aria-label="Questions per session"
                value={learningSettings.sessionSize}
                min={3} max={20} step={1}
                marks={[{ value: 3, label: '3' }, { value: 10, label: '10' }, { value: 20, label: '20' }]}
                onChange={(_, v) => setLearningSettings({ ...learningSettings, sessionSize: v as number })}
                onChangeCommitted={(_, v) => handleUpdateLearningSettings({ sessionSize: v as number })}
                disabled={learningSaving}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" gutterBottom>
                Max turns per question: {learningSettings.maxTurnsPerQuestion}
              </Typography>
              <Slider
                aria-label="Max turns per question"
                value={learningSettings.maxTurnsPerQuestion}
                min={1} max={10} step={1}
                marks={[{ value: 1, label: '1' }, { value: 5, label: '5' }, { value: 10, label: '10' }]}
                onChange={(_, v) => setLearningSettings({ ...learningSettings, maxTurnsPerQuestion: v as number })}
                onChangeCommitted={(_, v) => handleUpdateLearningSettings({ maxTurnsPerQuestion: v as number })}
                disabled={learningSaving}
              />
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Export your data</Typography>
          {exportError && <ErrorMessage message={exportError} />}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download everything you've built in Recall as a ZIP — your highlights and notes (Markdown
            and a Readwise-compatible CSV), plus your concepts and review progress (JSON). It's free
            and always available; your knowledge is yours to take anywhere.
          </Typography>
          <Button variant="outlined" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Preparing your export…' : 'Export my data'}
          </Button>
        </CardContent>
      </Card>

      <Card elevation={1} sx={{ borderColor: 'error.light', border: 1 }}>
        <CardContent>
          <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>Danger zone</Typography>
          {deleteError && <ErrorMessage message={deleteError} />}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete your account and all associated data. This action cannot be undone.
          </Typography>
          <Button variant="outlined" color="error" onClick={() => setShowDeleteDialog(true)}>
            Delete account
          </Button>
        </CardContent>
      </Card>

      {showDeleteDialog && (
        <ConfirmDialog
          title="Delete account"
          message="Are you sure you want to permanently delete your account? All your books, questions, and review history will be lost forever."
          confirmLabel={deleting ? 'Deleting...' : 'Delete my account'}
          onConfirm={handleDeleteAccount}
          onCancel={() => setShowDeleteDialog(false)}
          dangerous
        />
      )}
    </Box>
  )
}
